import { useEffect, useState } from 'react'
import { MdDelete, MdOpenInNew, MdSearch } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import {
    PayoutBatchDetailItem,
    getPayoutBatchItems,
} from '@/lib/api/financial/payout-batch-api'

const formatCurrency = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })

const PAGE_SIZE = 20

interface PayoutBatchItemsListProps {
    /** Solicitação agrupada cuja composição será listada. */
    batchId: number
    /**
     * Mostra o botão de tirar o lançamento da solicitação.
     *
     * Só o financeiro, antes de enviar para aprovação. Na fila, o aprovador confere e
     * decide — mudar a composição debaixo dele é mudar o valor que ele está aprovando.
     */
    allowRemove?: boolean
    onRemove?: (billingId: number) => void
    /** Lançamento em remoção: desabilita a linha enquanto a chamada não volta. */
    removingId?: number | null
    /** Trocar de valor recarrega a lista — use depois de remover um lançamento. */
    reloadToken?: number
    /**
     * Totais do lote inteiro (não da página), para o cabeçalho de quem usa a lista.
     *
     * Precisa ser **estável** (`useCallback`), senão cada render dispara uma carga.
     */
    onSummary?: (summary: { total: number; totalAmount: number }) => void
}

/**
 * Os repasses que compõem uma solicitação agrupada.
 *
 * A solicitação mostra um valor somado de um período inteiro — aprovar ou pagar sem
 * ver a composição é aprovar um número. Cada linha abre o lançamento individual, que
 * continua existindo com o rastro do atendimento que o originou.
 *
 * **Pagina no banco e busca por lançamento ou descrição.** A lista vinha inteira e era
 * renderizada de uma vez; num lote de mil atendimentos isso trava a tela justamente na
 * conferência, e achar uma linha específica é rolagem no escuro.
 *
 * Usada em três lugares com a mesma leitura: a conferência do aprovador (só leitura), o
 * detalhe do lançamento e a configuração do financeiro antes de enviar (com remover).
 */
export default function PayoutBatchItemsList({
    batchId,
    allowRemove = false,
    onRemove,
    removingId = null,
    reloadToken = 0,
    onSummary,
}: PayoutBatchItemsListProps) {
    const navigate = useNavigate()

    const [items, setItems] = useState<PayoutBatchDetailItem[]>([])
    const [page, setPage] = useState(1)
    const [pageCount, setPageCount] = useState(1)
    const [total, setTotal] = useState(0)
    const [search, setSearch] = useState('')
    const [term, setTerm] = useState('')
    const [loading, setLoading] = useState(true)

    // Busca só depois que o operador para de digitar: cada tecla seria uma consulta.
    useEffect(() => {
        const timer = setTimeout(() => {
            setTerm(search)
            setPage(1)
        }, 400)
        return () => clearTimeout(timer)
    }, [search])

    useEffect(() => {
        let active = true
        setLoading(true)
        getPayoutBatchItems(batchId, {
            page,
            pageSize: PAGE_SIZE,
            search: term || undefined,
        })
            .then(res => {
                if (!active) return
                const result = res.result
                setItems(result?.items ?? [])
                setTotal(result?.total ?? 0)
                setPageCount(result?.pageCount ?? 1)
                // Sem busca, o total é o do lote — é o que o cabeçalho anuncia. Com
                // busca, seria o do recorte, e o consumidor passaria a exibir um
                // número que não é o da solicitação.
                if (!term && result) {
                    onSummary?.({
                        total: result.total,
                        totalAmount: result.totalAmount,
                    })
                }
            })
            .catch(() => {
                if (!active) return
                setItems([])
                setTotal(0)
                setPageCount(1)
            })
            .finally(() => {
                if (active) setLoading(false)
            })
        return () => {
            active = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId, page, term, reloadToken])

    return (
        <div className="flex flex-col gap-2">
            {(total > PAGE_SIZE || term) && (
                <label className="input input-bordered input-sm flex items-center gap-2">
                    <MdSearch className="text-base-content/50" />
                    <input
                        type="text"
                        className="grow"
                        placeholder="Buscar por nº do lançamento ou descrição"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </label>
            )}

            {loading ? (
                <div className="py-10 text-center">
                    <span className="loading loading-spinner loading-md" />
                </div>
            ) : items.length === 0 ? (
                <div className="py-10 text-center text-base-content/60">
                    {term
                        ? 'Nenhum repasse encontrado para esta busca.'
                        : 'Nenhum repasse vinculado a esta solicitação.'}
                </div>
            ) : (
                items.map(item => (
                    <div
                        key={item.billingId}
                        className="rounded-lg border border-base-300/70 p-3 flex items-start justify-between gap-3"
                    >
                        <div className="min-w-0">
                            <div className="text-sm font-medium break-words">
                                {item.description || `Lançamento #${item.billingId}`}
                            </div>
                            <div className="text-xs text-base-content/60 mt-0.5 flex items-center gap-1.5 flex-wrap">
                                <span>#{item.billingId}</span>
                                {/* O paciente é por quem o relatório do parceiro é
                                    organizado: sem ele a conferência é por data +
                                    procedimento, que colide no mesmo dia. */}
                                {item.clientName && (
                                    <>
                                        <span>·</span>
                                        <span>{item.clientName}</span>
                                    </>
                                )}
                                {item.serviceDate && (
                                    <>
                                        <span>·</span>
                                        <span>
                                            Atendimento em{' '}
                                            {formatDate(item.serviceDate)}
                                        </span>
                                    </>
                                )}
                                {item.dueDate && (
                                    <>
                                        <span>·</span>
                                        <span>Vence em {formatDate(item.dueDate)}</span>
                                    </>
                                )}
                                {/* Um item já pago dentro de lote pendente é sinal de que
                                    algo saiu por fora — o operador precisa ver antes de
                                    aprovar. */}
                                {item.status !== 'PENDING' &&
                                    item.status !== 'OVERDUE' && (
                                        <span className="badge badge-xs badge-warning">
                                            {item.status}
                                        </span>
                                    )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className="font-mono text-sm font-medium whitespace-nowrap">
                                {formatCurrency(item.amount)}
                            </span>
                            <button
                                type="button"
                                className="btn btn-xs btn-ghost"
                                title="Abrir lançamento"
                                onClick={() =>
                                    navigate(`/financial/billings/${item.billingId}`)
                                }
                            >
                                <MdOpenInNew />
                            </button>
                            {allowRemove && (
                                <button
                                    type="button"
                                    className="btn btn-xs btn-ghost text-error"
                                    title="Tirar desta solicitação. O lançamento continua em aberto e volta no próximo agrupamento."
                                    disabled={removingId === item.billingId}
                                    onClick={() => onRemove?.(item.billingId)}
                                >
                                    {removingId === item.billingId ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdDelete />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                ))
            )}

            {pageCount > 1 && (
                <div className="flex items-center justify-between gap-2 pt-1">
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                        Anterior
                    </button>
                    <span className="text-xs text-base-content/60">
                        Página {page} de {pageCount}
                    </span>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        disabled={page >= pageCount || loading}
                        onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                    >
                        Próxima
                    </button>
                </div>
            )}
        </div>
    )
}
